/**
 * One device's status as the production sees it (PLAN §7.2): a chip in the
 * status's colour — good, warning, bad, unknown, or offline when it is not
 * connected at all — with the driver type, the device's message and, when it
 * is not connected, when it was last heard from. (Not for a connected one: a
 * production republishes a device's status when it changes, not every time it
 * hears from it, so an idle, healthy device's `lastSeen` ages — "12 min ago"
 * on a good device reads as a fault. Found against real productions,
 * test:e2e:playout.)
 */
import { Badge } from '../ui/badge.js';
import type { DeviceStatusWire } from '@norna/api-contract/playout.js';

export type DeviceStatusChipProps = {
  readonly device: DeviceStatusWire;
  /** The browser's clock, for "last seen 12 s ago" (an estimate against the production's). */
  readonly nowMs: number;
};

/** What the chip says and in which tally colour: disconnected wins over any status. */
export function deviceTone(device: DeviceStatusWire): { readonly variant: 'success' | 'warning' | 'destructive' | 'offline' | 'outline'; readonly label: string } {
  if (!device.connected) return { variant: 'offline', label: 'Disconnected' };
  switch (device.status) {
    case 'good':
      return { variant: 'success', label: 'Good' };
    case 'warning':
      return { variant: 'warning', label: 'Warning' };
    case 'bad':
      return { variant: 'destructive', label: 'Bad' };
    case 'unknown':
      return { variant: 'outline', label: 'Unknown' };
  }
}

function lastSeen(lastSeenMs: number | null, nowMs: number): string {
  if (lastSeenMs === null) return 'never heard from';
  const seconds = Math.max(0, Math.round((nowMs - lastSeenMs) / 1000));
  if (seconds < 5) return 'just now';
  if (seconds < 120) return `${String(seconds)} s ago`;
  return `${String(Math.round(seconds / 60))} min ago`;
}

export function DeviceStatusChip({ device, nowMs }: DeviceStatusChipProps) {
  const tone = deviceTone(device);
  return (
    <div className="grid gap-0.5 rounded-md border px-2.5 py-1.5" data-testid="device-status" data-device-id={device.deviceId} data-state={device.connected ? device.status : 'disconnected'}>
      <div className="flex items-center gap-2">
        <span className="font-mono text-sm font-medium">{device.deviceId}</span>
        <Badge variant={tone.variant}>{tone.label}</Badge>
      </div>
      <span className="text-xs text-muted-foreground">
        {device.type}
        {device.connected ? null : ` · ${lastSeen(device.lastSeen, nowMs)}`}
      </span>
      {device.message === null ? null : <span className="text-xs">{device.message}</span>}
    </div>
  );
}
